// import { PUBLIC_LOCATION } from "../commander/Types";
import CommonConfig from "../config/common";
export * as fn from "./history";
// takes a "classes" object and class name keys /the rest of the params/
// and returns the list of the actual classes names
export const getClasses = (
  classes: { [name: string]: string },
  ...names: (string | undefined)[]
): string => {
  if (names.length === 0) return "";

  return names
    .filter((name) => name)
    .map((name) => name!.split(" ")) // name cannot be undefined because of the filter, hence the "!"
    .flat()
    .reduce((str: string, name: string) => {
      return str + " " + (classes[name] ?? name);
    }, "");
};

export const spliceURLs = (...urls: string[]): string => {
  return urls.join("/").replace(/([^:])(\/\/+)/g, "$1/");
};

export const toPrecision = (n: number, precision = 2) => {
  const d = 10 ** precision;
  return Math.round(n * d) / d;
};

export const doTranslation = (
  key: string,
  custom: { [key: string]: string },
  common: { [key: string]: string },
  ...values: string[]
): string => {
  const text = custom[key] || common[key] || key;
  if (!values) return text;

  return replaceInTemplate(text, ...values);
};
// replace placeholder of type {{n}} where n is an number;
// example:
// replaceInTemplate("first {{1}}, then {{2}}, then {{1}} again", "foo", "bar" );
// will return: "first foo, then bar then foo again";
export const replaceInTemplate = (
  template: string,
  ...values: string[] | never
): string => {
  if (!template || !values) return template;

  const regEx = new RegExp("{{(([^}][^}]?|[^}]}?)*)}}", "g");
  // const matches = [...template.matchAll(regEx)];
  const matches = template.matchAll(regEx);
  let result = template;
  let next = matches.next();

  while (!next.done) {
    const [placeholder, key] = next.value;
    const i = parseInt(key) - 1;
    const value = values[i] || "";

    result = result.replace(placeholder, value);
    next = matches.next();
  }

  return result;
};
export const dispatchEvent = (eventType: string, data: any = null) => {
  const event = new CustomEvent(eventType, { detail: data });
  window.dispatchEvent(event);
};

// takes object which values are boolean and returns a new object
// with the same keys but the opposite boolean value, for example:
// revertToggleConfig({foo: true, bar: false}) will return {foo: false, bar: true}
export const revertToggleConfig = (cfg: { [key: string]: boolean }) => {
  return Object.keys(cfg).reduce((obj, key) => {
    return { ...obj, [key]: !cfg[key] };
  }, {});
};

// checks if the passed parameter is an object
export const isObject = (obj: any) => {
  return typeof obj === "object" && obj !== null && !Array.isArray(obj);
};

// same as Object.assign but recursive, and returns a new object ,rather than extending the old one
export const assignRecursive = (a: any, b: any) => {
  if (typeof b === "undefined") return Object.assign({}, a);

  const keys = [...Object.keys(a), ...Object.keys(b)];
  return keys.reduce((o, key) => {
    if (isObject(a[key]) && isObject(b[key])) {
      o[key] = assignRecursive(a[key], b[key]);
    } else {
      o[key] = b[key] ?? a[key];
    }
    return o;
  }, {} as any);
};

type Rect = { top: number; bottom: number; left: number; right: number };
export const areCoordinatesInArea = (
  x: number,
  y: number,
  rect: Rect
): boolean => {
  return x >= rect.left && x <= rect.right && y <= rect.bottom && y >= rect.top;
};

export const pxToEm = (
  px: number | string,
  fontSize: number = CommonConfig.documentFontSize
): string => {
  if (typeof px === "string") return pxToEm(parseInt(px), fontSize);
  if (isNaN(px) || !fontSize) return "0em";

  return px / fontSize + "em";
};

const valueToHex = (n: number) =>
  Math.max(0, Math.min(n ?? 0, 255))
    .toString(16)
    .padStart(2, "0");

export const RGBtoHex = (r: number, g: number, b: number) => {
  return `#${valueToHex(r)}${valueToHex(g)}${valueToHex(b)}`;
};

export const hexToRGB = (hex: string, defaultValue = null) => {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);

  if (!result) return defaultValue;

  return result
    .slice(1)
    .map((n) => parseInt(n, 16))
    .join(", ");
};

export const parseNumber = (n: any, defaultVal: number): number => {
  if (typeof n === "number" && !isNaN(n)) return n;
  const maybeaNumber = parseFloat(n);

  return isNaN(maybeaNumber) ? defaultVal : maybeaNumber;
};

/* function to compore the changes in the game state, it takes the props form
 * the states that are going to be compared, as well the old /o/ and the new state /n/.
 * It is intent to be used with "useAppSelector" when we want to be sure that
 * a compoennt will be rerendered only if the specific part of the state has changed */
export const compareState = (props: string[], o: any, n: any) => {
  if (!o || !n) return o === n;

  return props.reduce((acc, prop) => {
    return acc && o[prop] === n[prop];
  }, true);
};

export const isPointInRect = (
  point: [number, number],
  rect: [number, number, number, number]
): boolean => {
  const [px, py] = point;
  const [x, y, w, h] = rect;

  return px >= x && px <= x + w && py >= y && py <= y + h;
};

export const transpose = (matrix: any[][]) => {
  return matrix[0].map((_, col) => matrix.map((row) => row[col]));
};

export const getUTCTime = (time: string): Date => {
  const timeStr = time.endsWith("Z") ? time : time + " UTC";
  return new Date(timeStr);
};

export const formatTime = (time: string): string => {
  const d = getUTCTime(time);

  if (isNaN(d.getDate())) return "Unknown Date";

  const h = d.getHours().toString().padStart(2, "0");
  const m = d.getMinutes().toString().padStart(2, "0");
  const s = d.getSeconds().toString().padStart(2, "0");
  const da = d.getDate().toString().padStart(2, "0");
  const mo = (d.getMonth() + 1).toString().padStart(2, "0");
  const y = d.getFullYear();

  return `${da}/${mo}/${y} ${h}:${m}:${s}`;
};

export const applyTheme = (theme: { colors: any; alphas?: any }) => {
  Object.keys(theme?.colors ?? {}).forEach((color: string) => {
    document.documentElement.style.setProperty(
      `--color-${color}`,
      hexToRGB(theme.colors[color], theme.colors[color])
    );
  });

  Object.keys(theme?.alphas ?? {}).forEach((alpha: string) => {
    document.documentElement.style.setProperty(
      `--alpha-${alpha}`,
      hexToRGB(theme.alphas[alpha], theme.alphas[alpha])
    );
  });
};

// transforms [2, 1, 1, 2] to [[0, 0, 0, 0],[0, 1, 1, 0], [1, 0, 0, 1]]
// or in other words the format used in game engine configuration file and the paytable
export const transformLine = (
  definition: number[],
  height: number
): number[][] => {
  const slot = Array(height)
    .fill(0)
    .map(() => Array(definition.length).fill(0));

  definition.forEach((index, i) => (slot[index][i] = 1));

  return slot;
};

export const transformLines = (
  data: number[][] | number[][][]
): number[][][] => {
  if (Array.isArray(data[0][0])) return data as number[][][];

  const maximum = Math.max(...(data as number[][]).flat());
  return (data as number[][]).map((row) => transformLine(row, maximum + 1));
};

export const arrayUniques = <T>(arr: T[]): T[] => Array.from(new Set(arr));

export const paskalToKebab = (str: string): string =>
  str.replace(/([a-z0–9])([A-Z])/g, "$1-$2").toLowerCase();

// it also converts kebab-case
export const snakeToCamel = (str: string) =>
  str.replace(/[^a-zA-Z0-9]+(.)/g, (_, chr) => chr.toUpperCase());

export const snakeToPascal = (str: string) =>
  str[0].toUpperCase() + snakeToCamel(str.substring(1));

// 50000 - > 50 thousands
// NOTE: after trillions returns scientific notation, does NOT handles singular forms
export const numberToReadableStr = (
  n: number,
  translations: any,
  precision = 2
): string => {
  if (n < 100) return n.toFixed(precision);

  const cfg = [
    [2, "hundred"],
    [3, "thousands"],
    [6, "millions"],
    [9, "billions"],
    [12, "trillions"],
    [15, null],
  ];

  const sciNotation = n.toExponential();
  const [coe, exp] = sciNotation.split("e");
  const p = 10 ** precision;
  const expodent = parseInt(exp);
  const coefficient = Math.round(parseFloat(coe) * p) / p;
  const [base, suffix] = cfg.reduce(
    (acc: any, [key, value]) =>
      key && expodent >= (key as number) ? [key, value] : acc,
    null
  );

  if (!suffix) {
    return sciNotation;
  } else {
    return `${coefficient * 10 ** (expodent - base)} ${doTranslation(
      suffix,
      {},
      translations
    )}`;
  }
};

export const wait = (time: number) =>
  new Promise((resolve) => {
    setTimeout(resolve, time * 1000);
  });

export const waitFrame = (): Promise<number> =>
  new Promise((resolve) => {
    requestAnimationFrame(resolve);
  });

export const hasTouch = () =>
  "ontouchstart" in window || navigator.maxTouchPoints > 0;

export const LANGS: {
  supported: string[];
  aliases: { [key: string]: string };
} = {
  supported: [
    "en",
    "sx",
    "ys",
    "de",
    "at",
    "el",
    "es",
    "et",
    "fi",
    "fr",
    "hu",
    "it",
    "ja",
    "lt",
    "lv",
    "nl",
    "no",
    "pl",
    "pt",
    "ro",
    "ru",
    "sv",
    "tr",
  ],
  aliases: {
    sx: "en_sc",
    ys: "en_sc",
    at: "de",
  },
};

export const validateLang = (
  language: string | null,
  // config: { supported: string[]; aliases: { [key: string]: string } },
  config = LANGS,
  defaultLang = "en"
) => {
  if (!language || !config.supported || !config.supported.length)
    return defaultLang;
  const _lang = language.toLowerCase();
  const lang = config.aliases[_lang] ?? _lang;

  if (!config.supported.includes(_lang)) {
    console.warn(`${language} is not supported, defaults to en`);
    return defaultLang;
  }
  return lang;
};

export const splitLang = (language: string, upperCase = true) => {
  const seperator = language.includes("_") ? "_" : "-";
  const caseMethod = upperCase ? "toUpperCase" : "toLowerCase";
  const [base] = language[caseMethod]().split(seperator);
  const full = language[caseMethod]();

  return [base, full];
};
